import { readFragment, ResultOf } from "gql.tada";
import { InspoCarouselSectionById } from "../../../queries/entries/InspoCarouselSectionById";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  MotionValue,
  useTransform,
  motion,
  useMotionValue,
  useAnimate,
  useAnimationFrame,
  useInView,
  AnimatePresence,
} from "motion/react";
import { usePrevious, useUpdateEffect, useWindowSize } from "@react-hookz/web";
import { animationCurves } from "../../../utils/animationCurves";
import { InspoSlideControls } from "./InspoSlideControls";
import { useIsDesktop } from "../../../hooks/useIsDesktop";
import { ProgressBar } from "./ProgressBar";
import { InspoSlideTitleContent } from "./InspoSlideTitleContent";
import { Button } from "../../shared/core/Button";
import { Link } from "../../shared/core/Link";
import { IconPause, IconPlay } from "@hawkins/assets";
import { LinkFragment } from "../../../queries/fragments/LinkFragment";
import { MarkdownLite } from "../../shared/MarkdownLite";

export const InspoCarousel = ({
  entry,
  fullFillPeriodMv,
}: {
  entry: NonNullable<
    ResultOf<typeof InspoCarouselSectionById>["inspoCarouselSection"]
  >;
  fullFillPeriodMv: MotionValue<number>;
}) => {
  const cta = readFragment(LinkFragment, entry.cta);
  // Overall container element
  const carouselRef = useRef<HTMLDivElement>(null);
  // This is just to memoize the querySelector, so that we don't have to query the DOM on animation frames
  const videoElementsRef = useRef<Array<HTMLVideoElement>>([]);

  const [slideIndex, setSlideIndex] = useState(0);
  const previousSlideIndex = usePrevious(slideIndex);
  // If the user manages the play state, we'll block autoplay until they hit play again
  const [userPlayState, setUserPlayState] = useState<"play" | "paused">("play");

  // Slide the container to the current video
  const videoContainerX = useMotionValue(0);
  // The progress bar percentage numerical value
  const videoProgressPercent = useMotionValue(0);

  const [, animate] = useAnimate();
  const isDesktop = useIsDesktop();
  const isInView = useInView(carouselRef);
  const windowSize = useWindowSize();

  // this is the entry animation for all the content on top of the video
  const detailEntry = useTransform(fullFillPeriodMv, [0, 0.5], [0, 1]);
  // this is the suppressed scroll effect when all the elements of this component are in view
  const detailExitParallax = useTransform(
    fullFillPeriodMv,
    [0, 1],
    isDesktop ? [100, -100] : [50, -50]
  );
  // this is the clipPath title animation when entering
  // (also has to be mapped with in-out animation of the current slide)
  const slideTitleMv = useTransform(fullFillPeriodMv, [0, 0.5], [-1, 0]);

  useEffect(() => {
    const carouselElement = carouselRef.current;
    if (!carouselElement) return;
    videoElementsRef.current = [...carouselElement.querySelectorAll("video")];
  }, [entry]);

  // the X value to set the container to, to reset to the current slide
  const resetX = useMemo(() => {
    return slideIndex * -document.body.clientWidth;
  }, [slideIndex]);
  // reset the container X value to match with the current slide
  const reset = useCallback(() => {
    animate(videoContainerX, resetX, animationCurves.long);
  }, [animate, videoContainerX, resetX]);
  useEffect(reset, [reset]);
  useUpdateEffect(() => reset(), [windowSize]);

  const nextSlide = useCallback(
    () =>
      setSlideIndex((index) => {
        if (
          index ===
          (entry.inspoCarouselItemsCollection?.items.length ?? 0) - 1
        ) {
          return 0;
        } else return index + 1;
      }),
    [entry]
  );
  const prevSlide = useCallback(() => {
    setSlideIndex((index) => {
      if (index === 0) {
        return (entry.inspoCarouselItemsCollection?.items.length ?? 0) - 1;
      } else return index - 1;
    });
  }, [entry]);

  const toggleUserPlayState = useCallback(() => {
    setUserPlayState((playState) => {
      const currentVideo = videoElementsRef.current[slideIndex];
      if (currentVideo) {
        currentVideo.pause();
      }
      return playState === "paused" ? "play" : "paused";
    });
  }, [slideIndex]);

  // Update the slide index or reset the current video
  const updateSlideIndex = useCallback(
    (newIndex: number) => {
      setSlideIndex((index) => {
        if (newIndex === index) {
          const currentVideo = videoElementsRef.current[slideIndex];
          currentVideo.currentTime = 0;
          animate(videoProgressPercent, 0, animationCurves.default);
        }
        return newIndex;
      });
    },
    [animate, slideIndex, videoProgressPercent]
  );

  // Ensure videos aren't playing when the carousel is not in view
  useEffect(() => {
    const currentVideo = videoElementsRef.current[slideIndex];
    if (!currentVideo) return;
    if (isInView) {
      if (userPlayState === "play") currentVideo.play();
    } else {
      currentVideo.pause();
    }
  }, [isInView, slideIndex, userPlayState]);

  // Ensure that the progress bar animates to reset when the slide changes
  useEffect(() => {
    if (slideIndex !== previousSlideIndex)
      animate(videoProgressPercent, 0, animationCurves.long);
  }, [animate, videoProgressPercent, slideIndex, previousSlideIndex]);

  // This ensures that videos that are not currently selected are not playing
  // and when we change slides, we pause the current video and play the video
  // for the incoming slide
  useUpdateEffect(() => {
    videoElementsRef.current.forEach((video, index) => {
      const isSelected = index === slideIndex;
      if (isSelected) {
        video.currentTime = 0;
        if (userPlayState === "play") video.play();
      } else {
        video.pause();
      }
    });
  }, [slideIndex]);

  // a lock to manage race conditions
  const slideWhichIsAnimatingOut = useRef<number | null>(null);
  // Animate the progress bar on animation frames
  useAnimationFrame(() => {
    if (!isInView) return;
    const currentVideo = videoElementsRef.current[slideIndex];
    const percentage = (currentVideo.currentTime / currentVideo.duration) * 100;
    // if we're animating back to zero, cancel the animation before getting past
    // the point in the video we've gotten to during the time of the animation
    if (videoProgressPercent.animation?.state === "running") {
      if (percentage > videoProgressPercent.get()) {
        videoProgressPercent.animation.cancel();
        videoProgressPercent.set(percentage);
      }
    } else {
      videoProgressPercent.set(percentage);
      if (
        typeof slideWhichIsAnimatingOut.current === "number" &&
        slideWhichIsAnimatingOut.current !== slideIndex
      ) {
        slideWhichIsAnimatingOut.current = null;
      }
      if (
        percentage === 100 &&
        slideWhichIsAnimatingOut.current !== slideIndex
      ) {
        slideWhichIsAnimatingOut.current = slideIndex;
        nextSlide();
      }
    }
  });

  return (
    <motion.div
      ref={carouselRef}
      className="size-full relative overflow-hidden touch-pan-y"
      onPan={(_, pan) => videoContainerX.set(resetX + pan.offset.x)}
      onPanEnd={(_, pan) => {
        const panThreshold = document.body.clientWidth / 3;
        if (Math.abs(pan.offset.x) > panThreshold) {
          if (pan.offset.x > 0) {
            prevSlide();
          } else {
            nextSlide();
          }
        } else reset();
      }}
    >
      {
        <>
          <motion.div
            className="flex h-full"
            style={{
              width:
                (entry.inspoCarouselItemsCollection?.items.length ?? 0) *
                document.body.clientWidth,
              x: videoContainerX,
            }}
          >
            {entry.inspoCarouselItemsCollection?.items.map((slide, index) => {
              return (
                <motion.video
                  key={index}
                  className="h-full object-cover"
                  style={{
                    width: `${
                      100 /
                      (entry.inspoCarouselItemsCollection?.items.length ?? 0)
                    }%`,
                  }}
                  muted
                  playsInline
                  src={slide?.inspoCarouselSlideVideoUrl?.url ?? ""}
                />
              );
            })}
          </motion.div>
          <div className="absolute top-0 size-full bg-brand-black/30" />
          <motion.div
            style={{ y: detailExitParallax }}
            className="absolute top-0 size-full container-base"
          >
            <motion.div
              style={{ opacity: detailEntry }}
              className="size-full flex flex-col gap-7 justify-center items-center relative"
            >
              <InspoSlideControls
                ariaLabels={
                  entry?.inspoCarouselItemsCollection?.items?.map(
                    (item) => item?.a11YAriaLabel ?? ""
                  ) ?? []
                }
                total={entry.inspoCarouselItemsCollection?.items?.length ?? 0}
                index={slideIndex}
                setIndex={updateSlideIndex}
              />
              {/* This overlapping-grid ensures that this element is the size of the largest piece of content that will render here */}
              <div className="grid grid-cols-1 grid-rows-1 justify-items-center items-center">
                {entry.inspoCarouselItemsCollection?.items?.map(
                  (item, index) => (
                    <div key={index} className="col-start-1 row-start-1">
                      <InspoSlideTitleContent
                        index={index}
                        currentIndex={slideIndex}
                        entranceMv={slideTitleMv}
                      >
                        <MarkdownLite>
                          {item?.inspoCarouselTitle ?? ""}
                        </MarkdownLite>
                      </InspoSlideTitleContent>
                    </div>
                  )
                )}
                <div className="absolute bottom-10 flex gap-4 dsk:grid w-full items-center grid-cols-3">
                  <div className=" justify-self-start">
                    <Button
                      variant="light-on-blur"
                      size="large"
                      aria-label={entry.a11YPauseAriaLabel ?? ""}
                      className="absolute bottom-0 left-0 aspect-square !px-4"
                      onClick={toggleUserPlayState}
                    >
                      <AnimatePresence mode="wait" initial={false}>
                        <motion.span
                          key={userPlayState}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={animationCurves.half}
                        >
                          {userPlayState === "play" ? (
                            <IconPause size="small" />
                          ) : (
                            <IconPlay size="small" />
                          )}
                        </motion.span>
                      </AnimatePresence>
                    </Button>
                  </div>
                  <div className="justify-self-center">
                    {cta && (
                      <Button asChild variant="light-on-blur" size="large">
                        <Link link={cta}>{cta.text}</Link>
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              <ProgressBar percent={videoProgressPercent} />
            </motion.div>
          </motion.div>
        </>
      }
    </motion.div>
  );
};
